// src/controllers/interviewer_controller.js
import { Controller } from "@hotwired/stimulus"
import { createAlert } from '../scripts/createAlert';
import AudioRecorder from 'audio-recorder-polyfill'
import WaveSurfer from 'wavesurfer.js'
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js'

let recordingWavesurfer, recordedWavesurfer, recorder, playbackInterval;

const VIDEO_RECORDING_TIMESLICE_MS = 250;
const AUDIO_RECORDING_TIMESLICE_MS = 250;
const VIDEO_BITRATE = 2500000;
const AUDIO_BITRATE = 128000;

export default class extends Controller {
  static targets = [
    "text", "recordButton", "pauseButton", "resumeButton", "playButton",
    "audioFile", "submitButton", "cancelButton",
    "radio", "contentContainer", "lengthContainer", "lengthIcon", "lengthText", "progress", "total",
    "errorContainer", "startVideoRecording", "stopVideoRecording", "noAudioMessageContainer",
    "videoElement", "videoFile", "tabPane", "videoSource", "videoSourceContainer",
    "videoError", "countdown",
    "questionAudio", "questionPlay", "questionPause", "waveFormContainer", "recordedAudioContainer",
    "imageInput", "savingModal", "existingVideoElement", "mobileNavigation"
  ]

  static values = {
    interval: Number,
    duration: String,
    bugReportPath: String,
    answerUrl: String,
    questionTargetLength: String
  }

  // error message caught when init'ing audio
  error;

  // track whether the text/audio/video file has changed
  answerHasChanged = false;

  // track recording state
  isRecording = false;

  previewStream = null;
  selectedDeviceId = null;
  mediaRecorder = null;
  videoRecordingChunks = [];
  recordedVideoUrl = null;

  connect() {
    // console.log('Hello from the Interviewer controller!', this.element);
    // console.log('mobile navigation', this.mobileNavigationTargets);
    // console.log('Saving modal', this.savingModalTarget);

    self.error = '';

    this.initAudio();

    // Enable the use of radio buttons to control tabs
    this.radioTargets.forEach((radio) => {
      radio.addEventListener('click', (event) => {
        this.toggleInputDisplay(event);
      });
    });

    // Add navigation protection
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    if (this.isVideoTabActive() && !this.hasSavedVideo()) {
      this.startPreview();
    }
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    this.revokeRecordedVideoUrl();
    this.stopPreview();
  }

  handleBeforeUnload = (event) => {
    if (this.isRecording || (this.answerHasChanged && !this.isRecording)) {
      event.preventDefault();
      const message = this.isRecording
        ? 'You have an active recording in progress. Are you sure you want to leave?'
        : 'You have unsaved changes. Are you sure you want to leave?';
      event.returnValue = message;
      return event.returnValue;
    }
  }

  toggleInputDisplay(event) {
    const tabId = event.target.id.replace("-radio", "");

    if (tabId !== 'video-answer' && !this.isRecording) {
      this.stopPreview();
    }

    this.tabPaneTargets.forEach((tab) => {
      tab.classList.remove('show', 'active');
    });

    document.getElementById(tabId).classList.add('show', 'active');

    if (tabId === 'video-answer' && this.hasVideoElementTarget && !this.isRecording && !this.hasSavedVideo()) {
      this.startPreview();
    }
  }

  hasSavedVideo() {
    return this.hasExistingVideoElementTarget;
  }

  isVideoTabActive() {
    if (!this.hasVideoElementTarget) return false;
    const videoTab = document.getElementById('video-answer');
    return videoTab?.classList.contains('active') || videoTab?.classList.contains('show');
  }

  getVideoConstraints() {
    const constraints = {
      width: { ideal: 1280, max: 1920 },
      height: { ideal: 720, max: 1080 },
      frameRate: { ideal: 24, max: 30 }
    };

    if (this.selectedDeviceId) {
      return { ...constraints, deviceId: { exact: this.selectedDeviceId } };
    }

    return constraints;
  }

  selectVideoMimeType() {
    const candidates = [
      'video/mp4; codecs="avc1.424028, mp4a.40.2"',
      'video/mp4',
      'video/webm; codecs=vp9,opus',
      'video/webm; codecs=vp8,opus',
      'video/webm'
    ];

    return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
  }

  extensionForMimeType(mimeType) {
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('webm')) return 'webm';
    return 'webm';
  }

  revokeRecordedVideoUrl() {
    if (this.recordedVideoUrl) {
      URL.revokeObjectURL(this.recordedVideoUrl);
      this.recordedVideoUrl = null;
    }
  }

  async startPreview() {
    if (!this.hasVideoElementTarget) return;
    if (!navigator.mediaDevices?.getUserMedia) return;

    this.clearVideoError();
    this.stopPreview();

    if (this.videoElementTarget.src) {
      this.videoElementTarget.removeAttribute('src');
    }

    try {
      this.previewStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: this.getVideoConstraints()
      });

      this.videoElementTarget.srcObject = this.previewStream;
      this.videoElementTarget.classList.remove('d-none');
      this.videoElementTarget.classList.add('d-block');
      this.videoElementTarget.muted = true;
      this.videoElementTarget.removeAttribute('controls');
      await this.videoElementTarget.play();

      await this.populateVideoSources();
    } catch (err) {
      console.error(err);
      this.showVideoError('Camera access was denied. Please allow camera access and try again.');
    }
  }

  stopPreview() {
    if (this.isRecording) return;

    if (this.previewStream) {
      this.previewStream.getTracks().forEach((t) => t.stop());
      this.previewStream = null;
    }

    if (this.hasVideoElementTarget && this.videoElementTarget.srcObject) {
      this.videoElementTarget.srcObject = null;
      if (!this.videoElementTarget.src) {
        this.videoElementTarget.classList.remove('d-block');
        this.videoElementTarget.classList.add('d-none');
      }
    }

    if (this.hasVideoSourceContainerTarget) {
      this.videoSourceContainerTarget.classList.add('d-none');
    }
  }

  async populateVideoSources() {
    if (!this.hasVideoSourceTarget) return;

    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter((d) => d.kind === 'videoinput');

    this.videoSourceTarget.innerHTML = '';
    videoDevices.forEach((device, index) => {
      const option = document.createElement('option');
      option.value = device.deviceId;
      option.textContent = device.label || `Camera ${index + 1}`;
      if (device.deviceId === this.selectedDeviceId) {
        option.selected = true;
      }
      this.videoSourceTarget.appendChild(option);
    });

    if (!this.selectedDeviceId && videoDevices.length > 0) {
      this.selectedDeviceId = videoDevices[0].deviceId;
      this.videoSourceTarget.value = this.selectedDeviceId;
    }

    if (this.hasVideoSourceContainerTarget) {
      const shouldShowVideoSource = videoDevices.length > 1 && !this.isRecording && !this.recordedVideoUrl;
      if (shouldShowVideoSource) {
        this.videoSourceContainerTarget.classList.remove('d-none');
      } else {
        this.videoSourceContainerTarget.classList.add('d-none');
      }
    }
  }

  changeVideoSource(event) {
    if (this.isRecording) return;

    this.selectedDeviceId = event.target.value;
    this.startPreview();
  }

  showVideoError(message) {
    if (!this.hasVideoErrorTarget) return;
    this.videoErrorTarget.textContent = message;
    this.videoErrorTarget.classList.remove('d-none');
    this.videoErrorTarget.classList.add('d-block');
  }

  clearVideoError() {
    if (!this.hasVideoErrorTarget) return;
    this.videoErrorTarget.textContent = '';
    this.videoErrorTarget.classList.remove('d-block');
    this.videoErrorTarget.classList.add('d-none');
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async showCountdown() {
    if (!this.hasCountdownTarget) return;

    for (const count of [3, 2, 1]) {
      this.countdownTarget.textContent = count;
      this.countdownTarget.style.display = 'flex';
      this.countdownTarget.style.alignItems = 'center';
      this.countdownTarget.style.justifyContent = 'center';
      this.countdownTarget.classList.remove('d-none');
      await this.sleep(1000);
    }

    this.countdownTarget.style.display = '';
    this.countdownTarget.classList.add('d-none');
  }

  async startRecordVideo() {
    // Re-record goes back to a live preview so the user can pick a camera before recording again
    if (this.recordedVideoUrl || this.hasExistingVideoElementTarget) {
      await this.resetVideoForRerecord();
      return;
    }

    await this.startWithCountdown();
  }

  async resetVideoForRerecord() {
    this.isRecording = false;

    if (this.hasExistingVideoElementTarget) {
      this.existingVideoElementTarget.remove();
    }

    this.revokeRecordedVideoUrl();

    if (this.hasVideoElementTarget) {
      this.videoElementTarget.removeAttribute('src');
      this.videoElementTarget.removeAttribute('controls');
    }

    this.startVideoRecordingTarget.innerHTML = "<i class='fas fa-video me-1'></i> Record";
    this.startVideoRecordingTarget.disabled = false;

    await this.startPreview();
  }

  async startWithCountdown() {
    this.isRecording = true;

    if (this.hasVideoSourceContainerTarget) {
      this.videoSourceContainerTarget.classList.add('d-none');
    }

    if (!this.previewStream) {
      await this.startPreview();
    }
    if (!this.previewStream) {
      this.isRecording = false;
      return;
    }

    await this.showCountdown();
    await this.beginRecording();
  }

  initAudio() {
    //check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Your browser does not support audio recording!');
      return;
    }

    // if we have an answer to display, "preview" it
    if (this.answerUrlValue) {
      this.previewAudio();
    }
  }

  textChangeListener(event) {
    this.answerHasChanged = true;
  }

  createWaveSurfer() {
    // Create an instance of WaveSurfer for recording
    if (recordingWavesurfer) {
      recordingWavesurfer.destroy()
    }

    recordingWavesurfer = WaveSurfer.create({
      container: '#waveFormContainer',
      mediaType: 'audio/mp4', // Specify the audio file type,
      backend: 'MediaElement', // add this too
      waveColor: '#0e1a2c',
      progressColor: '#FFD81B',
      // Set a bar width
      barWidth: 2,
      // Optionally, specify the spacing between bars
      barGap: 1,
      // And the bar radius
      barRadius: 2
    })

    // Initialize the Record plugin
    recorder = recordingWavesurfer.registerPlugin(RecordPlugin.create({ mimeType: 'audio/mp4', scrollingWaveform: true, renderRecordedAudio: false, mediaRecorderTimeslice: AUDIO_RECORDING_TIMESLICE_MS }))

    // When paused or ended, render the recorded audio
    // recorder.on('record-end', this.previewAudio.bind(this));
    recorder.on('record-pause', this.previewAudio.bind(this));
    recorder.on('record-progress', this.updateTotalTime.bind(this));
  }

  previewAudio(blob) {
    if (blob && !blob.size) {
      // console.log('Got a null blob, returning');
      return;
    }

    this.recordedAudioContainerTarget.innerHTML = '';

    // update the total time when previewing the audio
    this.progressTarget.classList.remove('d-none');
    this.progressTarget.classList.add('d-block');

    // reset the previous playback timer
    this.progressTarget.textContent = `00:00 /\u00A0`;

    // const container = document.querySelector('#recordings')
    const recordedUrl = blob ? URL.createObjectURL(blob) : this.answerUrlValue;

    // Create wavesurfer from the recorded audio
    recordedWavesurfer = WaveSurfer.create({
      container: '#recordedAudioContainer',
      mediaType: 'audio/mp4', // Specify the audio file type,
      backend: 'MediaElement', // isSafari ? 'WebAudio' : 'MediaElement'
      waveColor: '#0e1a2c',
      progressColor: '#EE868C', //red color
      url: recordedUrl,
      // Set a bar width
      barWidth: 2,
      // Optionally, specify the spacing between bars
      barGap: 1,
      // And the bar radius
      barRadius: 2
    })

    // hide the waveFormContainer
    this.waveFormContainerTarget.classList.remove('d-block');
    this.waveFormContainerTarget.classList.add('d-none');

    // show recordedAudioContainer
    this.recordedAudioContainerTarget.classList.remove('d-none');
    this.recordedAudioContainerTarget.classList.add('d-block');

    if (this.answerUrlValue) {
      // we are viewing a previously answered question, need to prep the audio player
      recordedWavesurfer.on('ready', () => {
        // console.log('Ready to play the recorded audio', recordedWavesurfer.getDuration());
        this.updateTotalTime(recordedWavesurfer.getDuration() * 1000);

        if (this.hasLengthContainerTarget) {
          this.lengthContainerTarget.classList.remove('d-none');
          this.lengthContainerTarget.classList.add('d-flex');
        }

        this.recordButtonTarget.innerHTML = "<i class='fas fa-microphone me-1'></i> Record again"
      });
    }

    this.playButtonTarget.removeAttribute('disabled');
    this.playButtonTarget.onclick = () => {
      recordedWavesurfer.playPause();
      this.updatePlaybackProgressTime();
      if(recordedWavesurfer.isPlaying()) {
        // console.log('Now playing, set the interval');
        playbackInterval = setInterval(this.updatePlaybackProgressTime.bind(this), 1000);
      }
    }
    recordedWavesurfer.on('pause', () => {
      this.playButtonTarget.innerHTML = '<i class="fas fa-play-circle fa-lg color-fanword-text"></i>';
      clearInterval(playbackInterval);
      // this.updatePlaybackProgressTime(recordedWavesurfer.getDuration());
    });
    recordedWavesurfer.on('play', () => (this.playButtonTarget.innerHTML = '<i class="fas fa-pause-circle fa-lg color-fanword-text"></i>'))

    // file attached to the hidden input field
    // console.log('Attaching file to audio input field')
    let file = new File([blob], "audio-interview.mp4", {
      lastModified: new Date().getTime(),
    });
    let container = new DataTransfer();
    container.items.add(file);
    this.audioFileTarget.files = container.files;
  }

  updateTotalTime(time) {
    const formattedTime = this.formatTime(time);

    this.totalTarget.textContent = formattedTime;

    this.updateLengthIndicator(time);
  }

  updatePlaybackProgressTime() {
    const timeInSeconds = Math.round(recordedWavesurfer.getCurrentTime()) || 0;

    // console.log('Updating progress time', timeInSeconds);

    const formattedTime = this.formatTime(timeInSeconds * 1000);
    this.progressTarget.textContent = `${formattedTime} /\u00A0`;
  }

  formatTime(timeInMilliSeconds) {
    if (timeInMilliSeconds === 0) {
      return '00:00';
    }

    // time will be in milliseconds, convert it to mm:ss format
    const formattedTime = [
      Math.floor((timeInMilliSeconds % 3600000) / 60000), // minutes
      Math.floor((timeInMilliSeconds % 60000) / 1000), // seconds
    ]
      .map((v) => (v < 10 ? '0' + v : v))
      .join(':');

    return formattedTime;
  }

  // Helper method to create warning message
  createWarningMessage(targetElement) {
    // Remove any existing warning message before creating a new one
    const existingWarning = document.getElementById('recording-warning');
    if (existingWarning) existingWarning.remove();

    // Add warning message
    const warningDiv = document.createElement('div');
    warningDiv.className = 'text-danger small mt-2';
    warningDiv.id = 'recording-warning';
    warningDiv.textContent = 'Please save before navigating away';
    targetElement.after(warningDiv);
  }

  // Helper method to disable navigation
  disableNavigation() {
    this.submitButtonTargets.forEach(elm => elm.disabled = true);
    this.mobileNavigationTargets.forEach(elm => elm.classList.add('pe-none'));
    // Disable all navigation links
    document.querySelectorAll('#interview-menu .nav-link').forEach(link => link.classList.add('pe-none'));
    // Disable record button

    if (this.hasRecordButtonTarget) {
      this.recordButtonTarget.disabled = true;
    }

    if (this.hasStartVideoRecordingTarget) {
      this.startVideoRecordingTarget.disabled = true;
    }
  }

  // Helper method to enable navigation
  enableNavigation() {
    this.submitButtonTargets.forEach(elm => elm.disabled = false);
    this.mobileNavigationTargets.forEach(elm => elm.classList.remove('pe-none'));
    // Re-enable all navigation links
    document.querySelectorAll('#interview-menu .nav-link').forEach(link => link.classList.remove('pe-none'));
  }

  async record(event) {
    // If we still have the data-confirm-swal attribute, this is the first click
    if (event.target.hasAttribute('data-confirm-swal')) {
      return; // Let the confirmation.js handle the confirmation dialog
    }

    // make sure we have a pristine wavesurfer instance every time we start recording
    this.createWaveSurfer();

    // clear out any interval timer from previous recordings
    clearInterval(playbackInterval);

    // hide the progress time if it was previously shown
    this.progressTarget.classList.remove('d-block');
    this.progressTarget.classList.add('d-none');

    // disable navigation and buttons during recording
    this.disableNavigation();

    // hide resume and cancel recording buttons
    this.resumeButtonTarget.classList.remove('d-block');
    this.resumeButtonTarget.classList.add('d-none');
    this.cancelButtonTarget.classList.remove('d-block');
    this.cancelButtonTarget.classList.add('d-none');

      // get selected device
      recorder.startRecording().then(() => {
        this.isRecording = true;
        this.answerHasChanged = true;

        this.createWarningMessage(this.pauseButtonTarget);

        // hide the no audio container
        this.noAudioMessageContainerTarget.classList.remove('d-block');
        this.noAudioMessageContainerTarget.classList.add('d-none');

        // hide the preview audio container
        this.recordedAudioContainerTarget.classList.remove('d-block');
        this.recordedAudioContainerTarget.classList.add('d-none');

        // show the waveFormContainer
        this.waveFormContainerTarget.classList.remove('d-none');
        this.waveFormContainerTarget.classList.add('d-block');

        // hide the record button
        this.recordButtonTarget.classList.remove('d-block');
        this.recordButtonTarget.classList.add('d-none');

        // show the pause/stop button
        this.pauseButtonTarget.classList.remove('d-none');
        this.pauseButtonTarget.classList.add('d-block');

        // disable the play button
        this.playButtonTarget.disabled = true;
        this.playButtonTarget.classList.remove('interviewer-red');

        // show the length indicator block
        if (this.hasLengthContainerTarget) {
          this.lengthContainerTarget.classList.remove('d-none');
          this.lengthContainerTarget.classList.add('d-flex');
        }
      }).catch((error) => {
        console.log('Error when starting recording process:', error);
        this.error = error;

        this.contentContainerTarget.classList.remove('d-block');
        this.contentContainerTarget.classList.add('d-none');

        this.errorContainerTarget.classList.remove('d-none');
        this.errorContainerTarget.classList.add('d-block');
      });

  }

  resume() {
    //check if there are any previous recordings and remove them
    // if (this.audioContainerTarget.firstElementChild?.tagName === 'AUDIO') {
    //   this.audioContainerTarget.firstElementChild.remove();
    // }

    // hide the resume and cancel buttons
    this.resumeButtonTarget.classList.remove('d-block')
    this.resumeButtonTarget.classList.add('d-none')
    this.cancelButtonTarget.classList.remove('d-block')
    this.cancelButtonTarget.classList.add('d-none')

    // hide the record button
    this.recordButtonTarget.classList.remove('d-block')
    this.recordButtonTarget.classList.add('d-none')

    // show the pause button
    this.pauseButtonTarget.classList.remove('d-none')
    this.pauseButtonTarget.classList.add('d-block')

    // hide the preview audio container
    this.recordedAudioContainerTarget.classList.remove('d-block');
    this.recordedAudioContainerTarget.classList.add('d-none');

    // show the waveFormContainer
    this.waveFormContainerTarget.classList.remove('d-none');
    this.waveFormContainerTarget.classList.add('d-block');

    // hide the progress time
    this.progressTarget.classList.remove('d-block');
    this.progressTarget.classList.add('d-none');

    // disable navigation
    this.disableNavigation();

    // start recording
    recorder.resumeRecording();
    this.isRecording = true;

    // Add warning message
    this.createWarningMessage(this.pauseButtonTarget);

    // this.startInternalRecordingTimer(parseInt(this.durationValue));
  }

  // This method is actually triggered from a button that is labeled "Stop"
  // because that is what users will probably understand it as. But if we
  // used the mediaRecorder.stop() method, it would set the state on the
  // recorder to "inactive" and it couldn't be restarted. So we pause it
  // instead technically, and tell the user they are stopping it.
  async pause() {
    // console.log('Pausing recording');

    //check if there are any previous recordings and remove them
    // if (this.audioContainerTarget.firstElementChild?.tagName === 'AUDIO') {
    //   this.audioContainerTarget.firstElementChild.remove();
    // }

    // pause recording
    recorder.pauseRecording();
    this.isRecording = false;

    // Remove the warning message
    const warningMessage = document.getElementById('recording-warning');
    if (warningMessage) {
      warningMessage.remove();
    }

    // stop the 1s progress tics
    clearInterval(this.intervalValue);

    // hide the pause button
    this.pauseButtonTarget.classList.remove('d-block')
    this.pauseButtonTarget.classList.add('d-none')

    // show the resume recording button
    this.resumeButtonTarget.classList.remove('d-none')
    this.resumeButtonTarget.classList.add('d-block')

    // show and configure the cancel button
    this.cancelButtonTarget.classList.remove('d-none')
    this.cancelButtonTarget.classList.add('d-block')
    this.cancelButtonTarget.setAttribute('data-confirm-swal', 'Are you sure?')
    this.cancelButtonTarget.setAttribute('data-text', 'Canceling will discard your recording.')

    // allow the play button to be clicked
    this.playButtonTarget.removeAttribute('disabled');
    this.playButtonTarget.classList.add('interviewer-red');

    // Enable navigation except for menu links while paused
    this.submitButtonTargets.forEach(elm => elm.disabled = false);
    // Keep menu navigation disabled to prevent accidental navigation without saving
    this.mobileNavigationTargets.forEach(elm => elm.classList.add('pe-none'));
    document.querySelectorAll('#interview-menu .nav-link').forEach(link => link.classList.add('pe-none'));

    // show the re-record button
    this.recordButtonTarget.classList.remove('d-none')
    this.recordButtonTarget.classList.add('d-block')
    this.recordButtonTarget.innerHTML = "<i class='fas fa-microphone me-1'></i> Record again"
    this.recordButtonTarget.removeAttribute('disabled');
    this.recordButtonTarget.setAttribute('data-confirm-swal', 'Are you sure?');
    this.recordButtonTarget.setAttribute('data-text', 'Re-recording will replace your previously recorded answer.');
  }

  handleRecordedVideoBlob(blob, mimeType) {
    if (!blob.size) {
      this.showVideoError('Recording failed — no video data was captured. Please try again.');
      this.enableNavigation();
      return;
    }

    this.revokeRecordedVideoUrl();
    this.recordedVideoUrl = URL.createObjectURL(blob);

    if (this.previewStream) {
      this.previewStream.getTracks().forEach((track) => track.stop());
      this.previewStream = null;
    }

    this.videoElementTarget.srcObject = null;
    this.videoElementTarget.src = this.recordedVideoUrl;
    this.videoElementTarget.setAttribute('controls', true);
    this.videoElementTarget.muted = false;
    this.videoElementTarget.classList.remove('d-none');
    this.videoElementTarget.classList.add('d-block');

    this.stopVideoRecordingTarget.classList.remove('d-block');
    this.stopVideoRecordingTarget.classList.add('d-none');

    this.startVideoRecordingTarget.classList.remove('d-none');
    this.startVideoRecordingTarget.classList.add('d-block');
    this.startVideoRecordingTarget.innerHTML = "<i class='fas fa-video me-1'></i> Re-record";
    this.startVideoRecordingTarget.disabled = false;

    if (this.hasVideoSourceContainerTarget) {
      this.videoSourceContainerTarget.classList.add('d-none');
    }

    const extension = this.extensionForMimeType(mimeType);
    const file = new File([blob], `video-message.${extension}`, {
      type: mimeType,
      lastModified: Date.now(),
    });
    const container = new DataTransfer();
    container.items.add(file);
    this.videoFileTarget.files = container.files;

    this.videoElementTarget.play();
    this.enableNavigation();
  }

  async beginRecording() {
    if (this.hasExistingVideoElementTarget) {
      this.existingVideoElementTarget.remove();
    }

    this.revokeRecordedVideoUrl();
    this.videoRecordingChunks = [];

    // disable navigation
    this.disableNavigation();

    const stream = this.previewStream;
    if (!stream) {
      this.enableNavigation();
      return;
    }

    this.videoElementTarget.srcObject = stream;
    this.videoElementTarget.classList.remove('d-none');
    this.videoElementTarget.classList.add('d-block');
    this.videoElementTarget.muted = true;
    this.videoElementTarget.removeAttribute('controls');

    const mimeType = this.selectVideoMimeType();
    const recorderOptions = {
      videoBitsPerSecond: VIDEO_BITRATE,
      audioBitsPerSecond: AUDIO_BITRATE
    };
    if (mimeType) {
      recorderOptions.mimeType = mimeType;
    }

    try {
      this.mediaRecorder = new MediaRecorder(stream, recorderOptions);
    } catch (error) {
      this.error = error;
      console.error(error);
      this.isRecording = false;
      this.enableNavigation();
      alert('Unable to start video recording on this browser/device.');
      return;
    }

    this.startVideoRecordingTarget.classList.remove('d-block');
    this.startVideoRecordingTarget.classList.add('d-none');

    this.stopVideoRecordingTarget.classList.remove('d-none');
    this.stopVideoRecordingTarget.classList.add('d-block');
    this.stopVideoRecordingTarget.disabled = false;

    this.mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event.error);
      this.error = event.error;
      this.isRecording = false;
      this.showVideoError('Recording failed. Please try again.');
      this.enableNavigation();
    };

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.videoRecordingChunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      // Defer assembly one tick so the final dataavailable event is always processed first
      // (some browsers deliver stop before the last chunk is pushed).
      setTimeout(() => {
        const recordedMimeType = this.mediaRecorder.mimeType || mimeType || 'video/webm';
        const blob = new Blob(this.videoRecordingChunks, { type: recordedMimeType });
        this.videoRecordingChunks = [];
        this.handleRecordedVideoBlob(blob, recordedMimeType);
      }, 0);
    };

    this.mediaRecorder.start(VIDEO_RECORDING_TIMESLICE_MS);
    this.isRecording = true;

    this.createWarningMessage(this.stopVideoRecordingTarget);
  }

  stopRecordVideo() {
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') return;

    // Flush any encoded data still in the MediaRecorder buffer before stopping.
    if (this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.requestData();
    }
    this.mediaRecorder.stop();
    this.isRecording = false;
    this.answerHasChanged = true;
  }

  attemptSubmit(event) {
    event.preventDefault();

    // console.log('Audio file', this.audioFileTarget);
    // console.log('Text', this.textTarget);
    // console.log('Video file', this.videoFileTarget);

    let audioPresent = true;
    if (this.hasRecordedAudioContainerTarget) {
      if (this.audioFileTarget.files.length === 0 && this.recordedAudioContainerTarget?.firstElementChild?.tagName !== 'AUDIO') {
        audioPresent = false;
      }
    }

    let textPresent = true;
    if (this.hasTextTarget) {
      if (this.textTarget.value === '') {
        textPresent = false;
      }
    }

    let videoPresent = true;
    if (this.hasVideoFileTarget) {
      if (this.videoFileTarget.files.length === 0) {
        videoPresent = false;
      }
    }

    if(!audioPresent && !textPresent && !videoPresent) {
      createAlert('info', 'Please record an answer before moving forward.');

      const modal = bootstrap.Modal.getOrCreateInstance(this.savingModalTarget);
      modal.hide();

      return;
    }

    if (this.hasRecordedAudioContainerTarget) {

      if (this.answerHasChanged) {
        document.querySelector('#form_changed').value = 'true';
      } else {
        // if the answer has not changed, we can submit the form right away
        document.querySelector('#form_changed').value = 'false';
        event.target.form.submit();
        return;
      }

      // For audio, we need to stop the audio player, receive the FULL blob of data, and append that to the form
      if (audioPresent) {
        this.stopRecordingAndAttachFile().then(() => {
          // Clear the change flag before submitting
          this.answerHasChanged = false;
          // Submit the form once the file is attached
          event.target.form.submit();
        }).catch((error) => {
          console.error("Error stopping recording or attaching file:", error);
        });
      } else {
        // Clear the change flag before submitting
        this.answerHasChanged = false;
        // For text and video, we can submit right away
        event.target.form.submit();
      }

    } else {

      if (this.answerHasChanged) {
        document.querySelector('#form_changed').value = 'true';
      }

      // Clear the change flag before submitting
      this.answerHasChanged = false;
      event.target.form.submit();
    }

  }

  stopRecordingAndAttachFile() {
    // console.log('Stopping recording and attaching file, returning promise');

    return new Promise((resolve, reject) => {

      // Listen for the recording stop event
      recorder.on("record-end", (blob) => {
        if (!blob) {
          reject(new Error("No audio blob generated."));
          return;
        }

        // console.log("Recording stopped, got the blob", blob);

        // Create and attach the file
        let file = new File([blob], "audio-interview.mp4", {
          lastModified: new Date().getTime(),
        });
        let container = new DataTransfer();
        container.items.add(file);
        this.audioFileTarget.files = container.files;

        // console.log("Audio file attached to input field");

        // Resolve the promise to continue submission
        resolve();
      });

      // Stop the recording
      recorder.stopRecording();
    });
  }

  sendBugReport() {
    const bugReport = {
      origin: window.location.href,
      error: this.error.toString(),
      cookiesEnabled: navigator.cookieEnabled,
      language: navigator.language,
      userAgent: navigator.userAgent,
      navigatorUAData: navigator.userAgentData,
      dimensions: { width: window.innerWidth, height: window.innerHeight }
    };

    fetch(this.bugReportPathValue, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bugReport)
    });

    createAlert('info', 'Bug Report Sent!');
  }

  updateLengthIndicator(duration) {
    // console.log('Updating length indicator with duration:', duration);
    // console.log('Target length:', this.questionTargetLengthValue);

    var good, better, best;
    switch (this.questionTargetLengthValue) {
      case 'short':
        good = 5;
        better = 10;
        best = 15;
        break;

      case 'medium':
        good = 15;
        better = 30;
        best = 60;
        break;

      case 'long':
        good = 30;
        better = 60;
        best = 120;
        break;

      default:
        good = 15;
        better = 30;
        best = 60;
        break;
    }

    duration = duration / 1000; // comes in miliseconds

    let icon, color, message;

    // determine level of progress
    if (duration < good) {
      icon = ["fa-regular", "fa-face-frown"];
      color = 'bg-fanword-negative'
      message = 'Needs to be longer';
    } else if(duration >= good && duration < better) {
      icon = ["fa-regular", "fa-face-meh"];
      color = 'bg-fanword-lightyellow'
      message = 'Getting there...';
    } else if(duration >= better && duration < best) {
      icon = ["fa-regular", "fa-face-smile"];
      color = 'bg-fanword-positive'
      message = 'Good answer!';
    } else if(duration >= best) {
      icon = ["fa-regular", "fa-face-grin-stars"];
      color = 'bg-fanword-yellow'
      message = 'GREAT answer!';
    }

    if (this.hasLengthIconTarget) {
      this.lengthIconTarget.classList.remove('bg-fanword-negative', 'bg-fanword-lightyellow', 'bg-fanword-positive', 'bg-fanword-yellow');
      this.lengthIconTarget.classList.add(color);

      const smiley = this.lengthIconTarget.querySelector('#smiley');
      smiley.classList.remove('fa-regular', 'fa-face-frown', 'fa-face-meh', 'fa-face-smile', 'fa-face-grin-stars');
      icon.forEach((i) => smiley.classList.add(i));

      this.lengthTextTarget.innerText = message;
    }
  }

  questionPlayAudio() {
    this.questionPlayTarget.classList.remove('d-block');
    this.questionPlayTarget.classList.add('d-none');

    this.questionPauseTarget.classList.remove('d-none');
    this.questionPauseTarget.classList.add('d-block');

    this.questionAudioTarget.addEventListener('ended', () => {
      this.questionPauseTarget.classList.remove('d-block');
      this.questionPauseTarget.classList.add('d-none');

      this.questionPlayTarget.classList.remove('d-none');
      this.questionPlayTarget.classList.add('d-block');
    });

    this.questionAudioTarget.play();
  }

  questionPauseAudio() {
    this.questionPauseTarget.classList.remove('d-block');
    this.questionPauseTarget.classList.add('d-none');

    this.questionPlayTarget.classList.remove('d-none');
    this.questionPlayTarget.classList.add('d-block');

    this.questionAudioTarget.pause();
  }

  selectImage() {
    this.imageInputTarget.click();
  }

  submitImage() {
    if (this.imageInputTarget.files.length === 0) {
      createAlert('info', 'Please select an image before submitting.');
      return;
    }

    const modal = new bootstrap.Modal(this.savingModalTarget);
    modal.show();

    this.imageInputTarget.form.submit();
  }

  cancelRecording(event) {
    // If we still have the data-confirm-swal attribute, this is the first click
    if (event.target.hasAttribute('data-confirm-swal')) {
      return; // Let the confirmation.js handle the confirmation dialog
    }

    // Reset recording state
    this.isRecording = false;
    this.answerHasChanged = false;

    // Stop and destroy the recorder
    if (recorder) {
      recorder.stopRecording();
      recordingWavesurfer.destroy();
    }

    // Hide all recording-related buttons except the initial record button
    this.resumeButtonTarget.classList.remove('d-block');
    this.resumeButtonTarget.classList.add('d-none');
    this.cancelButtonTarget.classList.remove('d-block');
    this.cancelButtonTarget.classList.add('d-none');
    this.pauseButtonTarget.classList.remove('d-block');
    this.pauseButtonTarget.classList.add('d-none');

    // Check if there was a previous recording (answerUrlValue exists)
    if (this.answerUrlValue) {
      // Show the previous recording's waveform
      this.previewAudio();

      // Update record button to show "Record again"
      this.recordButtonTarget.classList.remove('d-none');
      this.recordButtonTarget.classList.add('d-block');
      this.recordButtonTarget.innerHTML = "<i class='fas fa-microphone me-1'></i> Record again";
      this.recordButtonTarget.removeAttribute('disabled');
      this.recordButtonTarget.setAttribute('data-confirm-swal', 'Are you sure?');
      this.recordButtonTarget.setAttribute('data-text', 'Re-recording will replace your previously recorded answer.');

      // Hide the no audio message since we have a previous recording
      this.noAudioMessageContainerTarget.classList.remove('d-block');
      this.noAudioMessageContainerTarget.classList.add('d-none');
    } else {
      // Reset and show the record button in its initial state
      this.recordButtonTarget.classList.remove('d-none');
      this.recordButtonTarget.classList.add('d-block');
      this.recordButtonTarget.innerHTML = "<i class='fas fa-microphone me-1'></i> Record";
      this.recordButtonTarget.removeAttribute('disabled');

      // Reset the audio display
      this.waveFormContainerTarget.classList.remove('d-block');
      this.waveFormContainerTarget.classList.add('d-none');
      this.recordedAudioContainerTarget.classList.remove('d-block');
      this.recordedAudioContainerTarget.classList.add('d-none');
      this.noAudioMessageContainerTarget.classList.remove('d-none');
      this.noAudioMessageContainerTarget.classList.add('d-block');

      // Reset the progress display
      this.progressTarget.classList.remove('d-block');
      this.progressTarget.classList.add('d-none');
      this.totalTarget.textContent = '00:00';

      // Disable the play button
      this.playButtonTarget.setAttribute('disabled', 'disabled');
      this.playButtonTarget.classList.remove('interviewer-red');
    }

    // Remove the warning message
    const warningMessage = document.getElementById('recording-warning');
    if (warningMessage) {
      warningMessage.remove();
    }

    // Clear length indicator
    if (this.hasLengthContainerTarget) {
      this.lengthContainerTarget.classList.remove('d-flex');
      this.lengthContainerTarget.classList.add('d-none');
    }

    // Remove confirmation from record button
    this.recordButtonTarget.removeAttribute('data-confirm-swal');
    this.recordButtonTarget.removeAttribute('data-text');

    // Re-enable all navigation
    this.enableNavigation();

    // Clear the audio file input only if there's no previous recording
    if (!this.answerUrlValue) {
      this.audioFileTarget.value = '';
    }
  }

}
